import type { QueryClient } from '@tanstack/react-query';

/**
 * Query roots that hold the titles of tasks linked to a document, outside the `documents` root so
 * dropping them never resets the document itself. Both are share-gated
 * (`share-gated-cache-registry.ts`): never persisted, and cancelled then reset on access loss so a
 * mounted observer drops the title even when its refetch fails
 * (see docs/plans/2026-10-28-feat-document-checklist-items-to-tasks-plan.md, R27).
 *
 * Runtime-dependency-free, like `pii-query-key-roots.ts`: the registry imports it into the
 * eagerly-loaded persistence module.
 */
export const DOCUMENT_LINKED_TASKS_QUERY_KEY = 'document-linked-tasks';
/** The checklist pill read (`get_document_task_items`), one entry per document. */
export const DOCUMENT_TASK_ITEMS_QUERY_KEY = 'document-task-items';

export const DOCUMENT_TASK_TITLE_ROOTS = [
  [DOCUMENT_LINKED_TASKS_QUERY_KEY],
  [DOCUMENT_TASK_ITEMS_QUERY_KEY],
] as const;

export const documentTaskTitleKeys = {
  /** The linked sidebar's Tasks section (`useDocumentTasks`). */
  linkedTasks: (
    orgId: string | undefined,
    userId: string | undefined,
    documentId: string | undefined,
  ) => [DOCUMENT_LINKED_TASKS_QUERY_KEY, orgId, userId, documentId] as const,
  /** The checklist pills of one document. */
  items: (orgId: string | undefined, userId: string | undefined, documentId: string | undefined) =>
    [DOCUMENT_TASK_ITEMS_QUERY_KEY, orgId, userId, documentId] as const,
} as const;

/**
 * Drop every cached document task title, in one office or (no `orgId`) all of them. Cancel, so a
 * read authorised before the access loss cannot land after it; reset, not invalidate or remove, so
 * a mounted observer loses its data and refetches under current RLS. Both act in this tick (TanStack
 * cancels and resets synchronously), so a caller that does not await still drops the titles at once.
 */
export async function resetDocumentTaskTitles(
  queryClient: QueryClient,
  orgId?: string,
): Promise<void> {
  const keys = DOCUMENT_TASK_TITLE_ROOTS.map((root) => (orgId ? [...root, orgId] : [...root]));
  await Promise.all(
    keys.flatMap((queryKey) => [
      queryClient.cancelQueries({ queryKey }),
      queryClient.resetQueries({ queryKey }),
    ]),
  );
}

/**
 * Drop one office's cached document task titles now, without refetching: for a burst of task-change
 * pokes, where each poke may mean lost access but one refetch at the end of the burst is enough.
 * `Query.reset` cancels a read in flight before clearing, so its late response cannot land; a
 * mounted observer shows loading until the caller's refetch.
 */
export function dropDocumentTaskTitles(queryClient: QueryClient, orgId: string): void {
  for (const root of DOCUMENT_TASK_TITLE_ROOTS) {
    for (const query of queryClient.getQueryCache().findAll({ queryKey: [...root, orgId] })) {
      query.reset();
    }
  }
}

/** After this viewer links or unlinks a task on a document. */
export async function invalidateDocumentTaskTitles(
  queryClient: QueryClient,
  orgId: string | undefined,
  userId: string | undefined,
  documentId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: documentTaskTitleKeys.linkedTasks(orgId, userId, documentId),
    }),
    queryClient.invalidateQueries({
      queryKey: documentTaskTitleKeys.items(orgId, userId, documentId),
    }),
  ]);
}

/**
 * Refetch one office's document task titles, or every office's (no `orgId`): after a server write
 * that may move task links (a Pulse undo restores the document's `document_tasks` rows), or after
 * `dropDocumentTaskTitles`. Matched by office: the caller holds no viewer, and a browser caches only
 * its signed-in viewer's entries.
 */
export async function invalidateOrganizationDocumentTaskTitles(
  queryClient: QueryClient,
  orgId?: string,
): Promise<void> {
  await Promise.all(
    DOCUMENT_TASK_TITLE_ROOTS.map(
      async (root) =>
        await queryClient.invalidateQueries({ queryKey: orgId ? [...root, orgId] : [...root] }),
    ),
  );
}
